import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DaoConfig } from '@/types/daoConfig';
import { DEFAULT_DAO_CONFIG, fetchDaoConfig } from '@/api/dao/daoConfigApi';

interface DaoConfigState {
  /** Active DAO configuration containing dynamic terminology, currency, and sector settings */
  config: DaoConfig;
  /** Loading status */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;

  /** Fetch the DAO configuration from the server */
  loadDaoConfig: () => Promise<void>;
  /** Directly set the DAO configuration */
  setDaoConfig: (config: DaoConfig) => void;
  /** Partially update the active DAO config */
  updateDaoConfig: (partial: Partial<DaoConfig>) => void;
}

export const useDaoConfigStore = create<DaoConfigState>()(
  persist(
    (set) => ({
      config: DEFAULT_DAO_CONFIG,
      isLoading: false,
      error: null,

      loadDaoConfig: async () => {
        set({ isLoading: true, error: null });
        try {
          const config = await fetchDaoConfig();
          set({ config, isLoading: false });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to load DAO config',
            isLoading: false,
          });
        }
      },

      setDaoConfig: (config: DaoConfig) => {
        set({ config, error: null });
      },

      updateDaoConfig: (partial) => {
        set((state) => ({
          config: {
            ...state.config,
            ...partial,
            terminology: {
              ...state.config.terminology,
              ...(partial.terminology || {}),
            },
            currency: {
              ...state.config.currency,
              ...(partial.currency || {}),
            },
          },
        }));
      },
    }),
    {
      name: 'abako-dao-config-storage',
      version: 1,
      partialize: (state) => ({
        config: state.config,
      }),
    }
  )
);
